import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { TouchEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { useUserSession, useFinancialPreferences, useFinancialVisibility } from "shared/contexts/UserContext";
import { useCurrencyConversion } from "shared/contexts/CurrencyConversionContext";
import { useBudgetData } from "features/budget/hooks/useBudgetData";
import { useReducedMotionPreference } from "shared/hooks/useReducedMotionPreference";
import { useTranslation } from "shared/hooks/useTranslation";
import {
    isBudgetImpactingTransaction,
    netBalancesFromBudgetTotals,
    parseTxDate,
    totalsForMonth,
} from "features/budget/components/transactions/budget-transactions/personalTransactionUtils";
import { formatCurrencyAmount, languageToLocale } from "shared/money/balances";
import { buildPersonalGuideItems } from "lib/guideBuilders";
import { syncPersonalLocalGuideNotifications } from "lib/syncLocalGuideNotifications";
import { fetchMobileNotificationSettings } from "shared/mobile/api/Mobile";
import type { AppNotification } from "types/Notification";
import type { MobileNotificationSettings } from "types/MobileNotificationSettings";
import { toTimestamp } from "utils/dateUtils";
import { formatLocalDateParam } from "utils/localDate";
import type { BudgetView } from "features/budget/components/balances/BalanceBudget";

export const BUDGET_TAB_OVERVIEW = 0;
export const BUDGET_TAB_CALENDAR = 1;

function tabFromViewParam(view: string | null): number {
    return view === "calendar" ? BUDGET_TAB_CALENDAR : BUDGET_TAB_OVERVIEW;
}

const SWIPE_MIN_DISTANCE_PX = 60;
const SWIPE_HORIZONTAL_RATIO = 1.5;
export const BUDGET_CAROUSEL_SLIDE_PERCENT = "100%";
export const BUDGET_CAROUSEL_DURATION_S = 0.38;

export function useBudgetScreen() {
    const { profile } = useUserSession();
    const { userSettings } = useFinancialPreferences();
    const { balancesVisible } = useFinancialVisibility();
    const { convertDatedTotal } = useCurrencyConversion();
    const { t, currentLanguage } = useTranslation();
    const prefersReducedMotion = useReducedMotionPreference();

    const [searchParams, setSearchParams] = useSearchParams();
    const [tab, setTab] = useState(() => tabFromViewParam(searchParams.get("view")));
    const [addMovementOpen, setAddMovementOpen] = useState(false);

    useEffect(() => {
        setTab(tabFromViewParam(searchParams.get("view")));
    }, [searchParams]);

    useEffect(() => {
        if (searchParams.get("action") !== "add") return;
        setAddMovementOpen(true);
        const next = new URLSearchParams(searchParams);
        next.delete("action");
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    const {
        groupTransactions,
        groupPeople,
        allBudgetTxs,
        loading: budgetLoading,
        error: budgetError,
        refetch: refetchBudgetData,
        handleAddTransaction,
        handleUpdateTransaction,
        handleDeleteTransaction,
    } = useBudgetData();

    const now = useMemo(() => new Date(), []);
    const [selectedYear, setSelectedYear] = useState(now.getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
    const monthSlideDirRef = useRef<1 | -1>(1);

    const handleMonthChange = useCallback((year: number, month: number) => {
        setSelectedYear(year);
        setSelectedMonth(month);
    }, []);

    const calendarOpenDay = useMemo(() => {
        const dayParam = searchParams.get("day");
        if (!dayParam) return null;
        const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dayParam);
        if (!match) return null;
        const year = Number(match[1]);
        const month = Number(match[2]);
        const day = Number(match[3]);
        if (year !== selectedYear || month !== selectedMonth) return null;
        return day;
    }, [searchParams, selectedYear, selectedMonth]);

    useEffect(() => {
        const dayParam = searchParams.get("day");
        if (!dayParam) return;
        const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dayParam);
        if (!match) return;
        const year = Number(match[1]);
        const month = Number(match[2]);
        if (year !== selectedYear || month !== selectedMonth) {
            handleMonthChange(year, month);
        }
        if (searchParams.get("view") !== "overview") {
            setTab(BUDGET_TAB_CALENDAR);
        }
    }, [searchParams, selectedYear, selectedMonth, handleMonthChange]);

    const shiftMonth = useCallback(
        (delta: number) => {
            monthSlideDirRef.current = (delta >= 0 ? 1 : -1) as 1 | -1;
            let m = selectedMonth + delta;
            let y = selectedYear;
            while (m < 1) { m += 12; y -= 1; }
            while (m > 12) { m -= 12; y += 1; }
            handleMonthChange(y, m);
        },
        [selectedMonth, selectedYear, handleMonthChange],
    );

    const budgetPanelVariants = useMemo(
        () => ({
            initial: () => {
                if (prefersReducedMotion) return { opacity: 0, x: 0 };
                const d = monthSlideDirRef.current;
                return {
                    opacity: 0.92,
                    x: d > 0 ? BUDGET_CAROUSEL_SLIDE_PERCENT : `-${BUDGET_CAROUSEL_SLIDE_PERCENT}`,
                };
            },
            animate: { opacity: 1, x: 0 },
            exit: () => {
                if (prefersReducedMotion) return { opacity: 0, x: 0 };
                const d = monthSlideDirRef.current;
                return {
                    opacity: 0.92,
                    x: d > 0 ? `-${BUDGET_CAROUSEL_SLIDE_PERCENT}` : BUDGET_CAROUSEL_SLIDE_PERCENT,
                };
            },
        }),
        [prefersReducedMotion],
    );

    const monthLabel = useMemo(() => {
        const d = new Date(selectedYear, selectedMonth - 1, 1);
        let monthStr = d.toLocaleString(languageToLocale(currentLanguage), { month: "long" });
        monthStr = monthStr.charAt(0).toUpperCase() + monthStr.slice(1);
        return `${monthStr} ${selectedYear}`;
    }, [selectedYear, selectedMonth, currentLanguage]);

    const locale = useMemo(() => languageToLocale(currentLanguage), [currentLanguage]);

    const upcomingPaymentNotifications = useMemo(
        () => buildPersonalGuideItems({
            transactions: allBudgetTxs,
            balancesVisible,
            locale,
            language: currentLanguage,
            username: profile.username ?? "",
            now,
            t,
        }),
        [allBudgetTxs, balancesVisible, locale, currentLanguage, profile.username, now, t],
    );

    useEffect(() => {
        if (!Capacitor.isNativePlatform() || !profile.token) return;
        let cancelled = false;
        void (async () => {
            const remote = await fetchMobileNotificationSettings(profile.token).catch((): MobileNotificationSettings => ({}));
            if (cancelled) return;
            const allow = remote.personal !== false;
            await syncPersonalLocalGuideNotifications(upcomingPaymentNotifications, { allow });
        })();
        return () => { cancelled = true; };
    }, [profile.token, upcomingPaymentNotifications]);

    const displayedTotals = useMemo(
        () => totalsForMonth(allBudgetTxs, selectedYear, selectedMonth),
        [allBudgetTxs, selectedYear, selectedMonth],
    );

    const selectedMonthAnalyticsQuery = useMemo(() => ({
        dateFrom: new Date(selectedYear, selectedMonth - 1, 1),
        dateTo: new Date(selectedYear, selectedMonth, 0),
    }), [selectedYear, selectedMonth]);

    const headerBalance = useMemo(() => {
        const byCur = netBalancesFromBudgetTotals(displayedTotals);
        const curKeys = Object.keys(byCur);
        if (curKeys.length === 0) return { label: undefined as string | undefined, color: "success" as const };

        let net: number;
        if (curKeys.length === 1) {
            net = byCur[curKeys[0]];
        } else {
            const monthTxs = allBudgetTxs.filter((tx) => {
                if (!isBudgetImpactingTransaction(tx)) return false;
                const d = parseTxDate(tx);
                return !isNaN(d.getTime())
                    && d.getFullYear() === selectedYear
                    && d.getMonth() + 1 === selectedMonth;
            });
            net = convertDatedTotal(monthTxs.map((tx) => ({
                amount: ((tx.type ?? "expense") === "income" ? 1 : -1) * tx.amount,
                currency: tx.currency || "ARS",
                date: tx.date,
            })));
        }

        const formatted = formatCurrencyAmount(Math.abs(net), userSettings.displayCurrency);
        return {
            label: `${t("budget.thisMonthBalance")}: ${formatted}`,
            color: (net >= 0 ? "success" : "error") as "success" | "error",
        };
    }, [
        allBudgetTxs,
        convertDatedTotal,
        displayedTotals,
        selectedMonth,
        selectedYear,
        userSettings.displayCurrency,
        t,
    ]);

    const setActiveTab = useCallback(
        (nextTab: number) => {
            setTab((prevTab) => {
                monthSlideDirRef.current = (nextTab > prevTab ? 1 : -1) as 1 | -1;
                return nextTab;
            });
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                if (nextTab === BUDGET_TAB_CALENDAR) {
                    next.delete("view");
                } else {
                    next.set("view", "overview");
                    next.delete("day");
                }
                return next;
            });
        },
        [setSearchParams],
    );

    const handleViewChange = useCallback(
        (view: BudgetView) => {
            setActiveTab(view === "calendar" ? BUDGET_TAB_CALENDAR : BUDGET_TAB_OVERVIEW);
        },
        [setActiveTab],
    );

    const handleUpcomingPaymentAction = useCallback((item: AppNotification) => {
        if (item.id.startsWith("card-statement-due-")) {
            const dueTs = item.date ? toTimestamp(item.date) : 0;
            if (dueTs) {
                const date = new Date(dueTs);
                handleMonthChange(date.getFullYear(), date.getMonth() + 1);
                setSearchParams({ day: formatLocalDateParam(date) });
            }
            setActiveTab(BUDGET_TAB_CALENDAR);
            return;
        }

        const transaction = allBudgetTxs.find((tx) => tx._id === item.sourceId);
        const timestamp = transaction ? toTimestamp(transaction.date) : 0;
        if (!timestamp) {
            setActiveTab(BUDGET_TAB_CALENDAR);
            return;
        }
        const date = new Date(timestamp);
        handleMonthChange(date.getFullYear(), date.getMonth() + 1);
        setSearchParams({ day: formatLocalDateParam(date) });
        setActiveTab(BUDGET_TAB_CALENDAR);
    }, [allBudgetTxs, handleMonthChange, setActiveTab, setSearchParams]);

    const touchStartRef = useRef<{ x: number; y: number } | null>(null);

    const handleTouchStart = useCallback((event: TouchEvent<HTMLDivElement>) => {
        const touch = event.changedTouches[0];
        touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    }, []);

    const handleTouchEnd = useCallback((event: TouchEvent<HTMLDivElement>) => {
        const start = touchStartRef.current;
        touchStartRef.current = null;
        if (!start) return;

        const touch = event.changedTouches[0];
        const deltaX = touch.clientX - start.x;
        const deltaY = touch.clientY - start.y;
        const absX = Math.abs(deltaX);
        const absY = Math.abs(deltaY);

        const isHorizontalSwipe =
            absX >= SWIPE_MIN_DISTANCE_PX &&
            absX > absY * SWIPE_HORIZONTAL_RATIO;

        if (!isHorizontalSwipe) return;

        shiftMonth(deltaX < 0 ? 1 : -1);
    }, [shiftMonth]);

    return {
        t,
        profile,
        tab,
        setActiveTab,
        addMovementOpen,
        setAddMovementOpen,
        budget: {
            budgetLoading,
            budgetError,
            refetchBudgetData,
            handleAddTransaction,
            handleUpdateTransaction,
            handleDeleteTransaction,
            groupTransactions,
            groupPeople,
            allBudgetTxs,
        },
        month: {
            selectedYear,
            selectedMonth,
            monthLabel,
            shiftMonth,
            handleMonthChange,
            displayedTotals,
            selectedMonthAnalyticsQuery,
            calendarOpenDay,
        },
        carousel: {
            budgetPanelVariants,
            prefersReducedMotion,
        },
        notifications: { upcomingPaymentNotifications, handleUpcomingPaymentAction },
        headerBalance,
        handleViewChange,
        swipe: { handleTouchStart, handleTouchEnd },
    };
}
